/**
 * The file types the Actions folder's picker offers — the extensions the backend team
 * named for the upload endpoint (comment on PSTUDIO-3847, 2026-08-10).
 *
 * History: `.key` and `.cert` were put in this list and taken out again as unsupported on
 * the same day, 2026-02-27 (`75d3cc7256`). They come back here on the strength of the
 * backend team's later comment above; `.cert` is not among them, `.cer` is.
 *
 * `.md` is the one entry that did not come from that comment: PSTUDIO-3847 asks for
 * markdown files on this tab, and it is added to the Actions folder only, as `.cer`/`.key`
 * were. Nothing was uploaded with it against a backend while this was written, so whether
 * the server takes it is not established.
 *
 * LIMITATION: `accept` only filters the OS file dialog, and nothing in this app checks a
 * type on upload. A user can still pick "All files". One manual upload to the main
 * environment (2026-10-07, Actions folder only, one file per type) was answered 200 by
 * `agent-manager-sync` for every file tried, so no server-side refusal of types outside
 * this list was observed. That is one check on one environment, not a statement about
 * what the server enforces.
 */
export const ACTION_FILE_EXTENSIONS = [
  '.py',
  '.csv',
  '.json',
  '.pem',
  '.crt',
  '.cer',
  '.key',
  '.md',
] as const;

export const ACTION_FILE_ACCEPT = ACTION_FILE_EXTENSIONS.join(',');

/**
 * The Configuration folder's picker, unchanged by PSTUDIO-4416: that ticket asked for the
 * Actions folder, and certificates and keys were not tried against Configuration.
 */
export const CONFIG_FILE_ACCEPT = '.py,.csv,.json,.pem,.crt';

/**
 * Types the row's edit button must not open. The editor decodes a file as UTF-8 text and
 * saves the text back, which is lossless for PEM but destroys a binary DER certificate
 * (measured: 793 bytes in, 1264 out). Only the two types this ticket added are listed;
 * `.pem` and `.crt` keep the behaviour they had.
 */
const NON_EDITABLE_EXTENSIONS = ['.cer', '.key'];

export const isEditableActionFile = (fileName: string): boolean => {
  const name = fileName.toLowerCase();
  return !NON_EDITABLE_EXTENSIONS.some((extension) => name.endsWith(extension));
};
